/**
 * Support Ticket Status
 * Matches Zoho FSM Request statuses
 */
export type SupportTicketStatus = 'open' | 'in_progress' | 'resolved' | 'closed';

/**
 * Support Ticket Priority
 * Set by support staff, not by users
 */
export type SupportTicketPriority = 'low' | 'medium' | 'high' | 'urgent';

/**
 * Support Ticket Category
 */
export type SupportTicketCategory =
  | 'Technical Issue'
  | 'Billing Question'
  | 'Equipment Problem'
  | 'Training Request'
  | 'Account Issue'
  | 'Feature Request'
  | 'Other';

/**
 * Ticket channel - where the ticket originated
 */
export type TicketChannel = 'email' | 'web' | 'phone' | 'chat' | 'api' | 'widget' | 'forums' | 'twitter' | 'facebook';

/**
 * SLA status tracking
 */
export type SlaStatus = 'on_track' | 'at_risk' | 'breached';

/**
 * Thread type - distinguishes between different kinds of conversation entries
 */
export type ThreadType = 'reply' | 'comment' | 'note';

/**
 * Thread direction - indicates who initiated the message
 */
export type ThreadDirection = 'inbound' | 'outbound';

/**
 * Author type - who created the thread entry
 */
export type AuthorType = 'agent' | 'contact' | 'system';

/**
 * Support Ticket model
 * Represents a customer support ticket/request.
 * Supports bi-directional sync with Zoho Desk.
 */
export interface SupportTicket {
  id: string;
  ticketNumber: string | null;
  zohoDeskTicketNumber?: string | null;

  // Organization and drone relationships
  organizationId: string;
  droneId: string | null;

  // Drone details (populated on fetch)
  droneName?: string | null;
  droneSerialNumber?: string | null;

  // Ticket content
  subject: string;
  description: string;
  category: SupportTicketCategory | null;
  subCategory?: string | null;
  channel?: TicketChannel | null;

  // Status and priority (priority set by support staff)
  status: SupportTicketStatus;
  priority: SupportTicketPriority;

  // Assignment (from Zoho)
  assignedToName: string | null;
  assignedToEmail?: string | null;

  // SLA tracking
  dueDate?: string | null;
  slaStatus?: SlaStatus | null;

  // Resolution tracking
  closedAt?: string | null;
  resolution?: string | null;
  responseTimeSeconds?: number | null;
  resolutionTimeSeconds?: number | null;

  // Zoho Desk link
  zohoDeskWebUrl?: string | null;

  // User who created the ticket
  createdByProfileId: string;
  createdByName?: string;

  // Audit fields
  createdAt: string;
  updatedAt: string;
}

/**
 * Support Ticket Thread (conversation entry)
 */
export interface SupportTicketThread {
  id: string;
  ticketId: string;
  threadType: ThreadType;
  direction: ThreadDirection | null;
  channel: string | null;
  content: string;
  contentType: string;
  summary: string | null;
  isPublic: boolean;
  authorType: AuthorType | null;
  authorName: string | null;
  authorEmail: string | null;
  createdByProfileId: string | null;
  createdByName?: string | null;
  createdAt: string;
}

/**
 * Support Ticket Attachment
 */
export interface SupportTicketAttachment {
  id: string;
  ticketId: string;
  fileName: string;
  fileSize: number | null;
  formattedFileSize: string;
  fileType: string | null;
  fileExtension: string | null;
  downloadUrl: string | null;
  previewUrl: string | null;
  uploadedByName: string | null;
  uploadedByEmail: string | null;
  uploadedAt: string | null;
  isImage: boolean;
  isPdf: boolean;
  fileIcon: string;
  createdAt: string;
}

/**
 * Support Ticket create payload
 */
export interface SupportTicketCreate {
  subject: string;
  description: string;
  category?: SupportTicketCategory;
  droneId?: string;
  organizationId: string;
}

/**
 * Support Ticket update payload
 */
export interface SupportTicketUpdate {
  subject?: string;
  description?: string;
  category?: SupportTicketCategory;
  droneId?: string;
}

/**
 * Support Ticket list query parameters
 */
export interface SupportTicketListQuery {
  status?: SupportTicketStatus;
  priority?: SupportTicketPriority;
  category?: SupportTicketCategory;
  droneId?: string;
  search?: string;
  page?: number;
  limit?: number;
}

/**
 * Support Ticket list response
 */
export interface SupportTicketListResponse {
  tickets: SupportTicket[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

/**
 * Support Ticket status counts
 */
export interface SupportTicketStatusCounts {
  open: number;
  in_progress: number;
  resolved: number;
  closed: number;
}

/**
 * Category options for UI
 */
export const SUPPORT_TICKET_CATEGORIES: { value: SupportTicketCategory; label: string }[] = [
  { value: 'Technical Issue', label: 'Technical Issue' },
  { value: 'Equipment Problem', label: 'Equipment Problem' },
  { value: 'Billing Question', label: 'Billing Question' },
  { value: 'Training Request', label: 'Training Request' },
  { value: 'Account Issue', label: 'Account Issue' },
  { value: 'Feature Request', label: 'Feature Request' },
  { value: 'Other', label: 'Other' }
];

/**
 * Priority display info
 */
export const SUPPORT_TICKET_PRIORITIES: { value: SupportTicketPriority; label: string; color: string }[] = [
  { value: 'low', label: 'Low', color: 'info' },
  { value: 'medium', label: 'Medium', color: 'secondary' },
  { value: 'high', label: 'High', color: 'warning' },
  { value: 'urgent', label: 'Urgent', color: 'danger' }
];

/**
 * Status display info
 */
export const SUPPORT_TICKET_STATUSES: { value: SupportTicketStatus; label: string; color: string }[] = [
  { value: 'open', label: 'Open', color: 'primary' },
  { value: 'in_progress', label: 'In Progress', color: 'warning' },
  { value: 'resolved', label: 'Resolved', color: 'success' },
  { value: 'closed', label: 'Closed', color: 'medium' }
];
